/**
 * The period-close checks as words in the interface's language.
 *
 * GET /v1/finance/dashboard (`periodCloseChecklist[].item`) and
 * GET /v1/finance/period-close (`checklist[].item`, `.message`) send these as
 * English text and no code (finance-overview.controller.ts,
 * `readCloseChecks`): four fixed checks, and for a blocked one "<n> still
 * unposted", "<n> awaiting posting" or "<n> overdue". Until the API sends
 * codes (lesson 3, "Codes, not English"), they are matched here by their
 * exact text, in one place; anything else is shown as sent, never blank.
 */
import type { TFunction } from "@/lib/i18n";

const CHECKS: Record<string, string> = {
  "All journal entries posted": "finance.closeChecks.allEntriesPosted",
  "Supplier invoices posted": "finance.closeChecks.supplierInvoicesPosted",
  "Customer invoices posted": "finance.closeChecks.customerInvoicesPosted",
  "No overdue receivables": "finance.closeChecks.noOverdueReceivables",
};

const BLOCKERS: Record<string, string> = {
  "still unposted": "finance.closeChecks.stillUnposted",
  "awaiting posting": "finance.closeChecks.awaitingPosting",
  overdue: "finance.closeChecks.overdue",
};

/** A check's name: "All journal entries posted" → «…». */
export function closeCheckText(t: TFunction, item: string): string {
  const key = CHECKS[item];
  return key ? t(key, { defaultValue: item }) : item;
}

/** A blocked check's reason: "3 still unposted" → «3 …». */
export function closeBlockerText(t: TFunction, message: string): string {
  const match = /^(\d+) (still unposted|awaiting posting|overdue)$/.exec(
    message,
  );
  const key = match ? BLOCKERS[match[2]!] : undefined;
  return key
    ? t(key, { count: Number(match![1]), defaultValue: message })
    : message;
}
